import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { labResultsService, LabResult, LabResultType } from '../lib/labResultsService';
import {
  labResultAttachmentsService,
  LabResultAttachment,
} from '../lib/labResultAttachmentsService';
import { supabase } from '../lib/supabase';
import { getErrorMessage } from '../utils/errorMessages';

interface LabResultsPageProps {
  onBack?: () => void;
}

const TYPE_LABELS: Record<LabResultType, string> = {
  kalprotectyna: 'Kalprotectyna w kale',
  crp: 'CRP',
  inne: 'Inne',
};

export const LabResultsPage: React.FC<LabResultsPageProps> = ({ onBack }) => {
  const [results, setResults] = useState<LabResult[]>([]);
  const [attachmentsByResult, setAttachmentsByResult] = useState<Record<string, LabResultAttachment[]>>({});
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<{ id: string } | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'kalprotectyna' as LabResultType,
    typeOther: '',
    value: '',
    unit: '',
    notes: '',
  });
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [attachments, setAttachments] = useState<LabResultAttachment[]>([]);
  const [attachmentLoading, setAttachmentLoading] = useState(false);
  const [uploadingOnly, setUploadingOnly] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user: u } }) => {
      setUser(u ?? null);
      if (u) {
        labResultsService
          .getAll(u.id)
          .then(async (res) => {
            setResults(res);
            const ids = res.map((r) => r.id);
            const byResult = await labResultAttachmentsService.getByLabResultIds(ids);
            setAttachmentsByResult(byResult);
          })
          .catch((err) => {
            console.error(err);
            toast.error(getErrorMessage(err));
          })
          .finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });
  }, []);

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      type: 'kalprotectyna',
      typeOther: '',
      value: '',
      unit: labResultsService.getDefaultUnit('kalprotectyna'),
      notes: '',
    });
    setEditingId(null);
    setPendingFiles([]);
    setAttachments([]);
  };

  const loadAttachments = (labResultId: string) => {
    labResultAttachmentsService
      .getByLabResultId(labResultId)
      .then(setAttachments)
      .catch(() => setAttachments([]));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (formData.type === 'inne' && !formData.typeOther?.trim()) {
      toast.error('Wpisz, czego dotyczy ten wynik');
      return;
    }
    const numValue = parseFloat(formData.value.replace(',', '.'));
    if (isNaN(numValue) || numValue < 0) {
      toast.error('Podaj prawidłową wartość');
      return;
    }

    try {
      if (editingId) {
        await labResultsService.update(editingId, user.id, {
          date: formData.date,
          type: formData.type,
          typeOther: formData.type === 'inne' ? formData.typeOther : undefined,
          value: numValue,
          unit: formData.unit || undefined,
          notes: formData.notes || undefined,
        });
        toast.success('Wynik zaktualizowany');
      } else {
        const created = await labResultsService.create(user.id, {
          date: formData.date,
          type: formData.type,
          typeOther: formData.type === 'inne' ? formData.typeOther : undefined,
          value: numValue,
          unit: formData.unit || undefined,
          notes: formData.notes || undefined,
        });
        for (const file of pendingFiles) {
          try {
            await labResultAttachmentsService.upload(user.id, created.id, file);
          } catch (e) {
            toast.error(getErrorMessage(e));
          }
        }
        toast.success('Wynik dodany' + (pendingFiles.length ? ` (${pendingFiles.length} załączników)` : ''));
      }
      const updated = await labResultsService.getAll(user.id);
      const byResult = await labResultAttachmentsService.getByLabResultIds(updated.map((r) => r.id));
      setResults(updated);
      setAttachmentsByResult(byResult);
      setShowForm(false);
      resetForm();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleEdit = (r: LabResult) => {
    setFormData({
      date: r.date,
      type: r.type,
      typeOther: r.typeOther ?? '',
      value: String(r.value),
      unit: r.unit ?? labResultsService.getDefaultUnit(r.type),
      notes: r.notes ?? '',
    });
    setEditingId(r.id);
    setPendingFiles([]);
    loadAttachments(r.id);
    setShowForm(true);
  };

  const handleFileOnlyUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!user || files.length === 0) return;
    setUploadingOnly(true);
    try {
      const created = await labResultsService.createMinimal(user.id);
      for (const file of files) {
        try {
          await labResultAttachmentsService.upload(user.id, created.id, file);
        } catch (err) {
          toast.error(getErrorMessage(err));
        }
      }
      const updated = await labResultsService.getAll(user.id);
      const byResult = await labResultAttachmentsService.getByLabResultIds(updated.map((r) => r.id));
      setResults(updated);
      setAttachmentsByResult(byResult);
      toast.success(`Zapisano ${files.length} plik(ów)`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUploadingOnly(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!user) return;
    if (editingId) {
      setAttachmentLoading(true);
      for (const file of files) {
        try {
          const att = await labResultAttachmentsService.upload(user.id, editingId, file);
          setAttachments((prev) => [...prev, att]);
          toast.success(`Dodano: ${file.name}`);
        } catch (err) {
          toast.error(getErrorMessage(err));
        }
      }
      setAttachmentLoading(false);
    } else {
      setPendingFiles((prev) => [...prev, ...files]);
      toast.success(`Dodano ${files.length} plik(ów)`);
    }
  };

  const removePendingFile = (idx: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleDeleteAttachment = async (att: LabResultAttachment) => {
    if (!user) return;
    try {
      await labResultAttachmentsService.delete(att, user.id);
      setAttachments((prev) => prev.filter((a) => a.id !== att.id));
      toast.success('Załącznik usunięty');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDownloadAttachment = async (att: LabResultAttachment) => {
    try {
      const url = await labResultAttachmentsService.getSignedUrl(att.filePath);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDelete = async (id: string) => {
    if (!user || !window.confirm('Czy na pewno usunąć ten wynik?')) return;
    try {
      await labResultsService.delete(id, user.id);
      setResults((prev) => prev.filter((r) => r.id !== id));
      setAttachmentsByResult((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      toast.success('Wynik usunięty');
      if (editingId === id) {
        setShowForm(false);
        resetForm();
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  if (!user) return null;

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      {onBack && (
        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 20,
            padding: '8px 0',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: 14,
            color: '#64748b',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Wstecz
        </button>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0f172a' }}>Wyniki badań</h1>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          style={{
            padding: '10px 20px',
            borderRadius: 10,
            border: 'none',
            background: 'linear-gradient(135deg, #6B7FFF 0%, #5A6EE6 100%)',
            color: 'white',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          + Dodaj wynik
        </button>
      </div>

      <p style={{ margin: '0 0 24px', fontSize: 14, color: '#64748b' }}>
        Zapisz wyniki badań (np. kalprotectyna, CRP) – przydatne do raportów dla lekarza.
      </p>

      <div
        style={{
          marginBottom: 24,
          padding: 24,
          borderRadius: 16,
          border: '2px dashed #cbd5e1',
          background: '#f8fafc',
          textAlign: 'center',
        }}
      >
        <p style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#475569' }}>
          Masz wynik w PDF lub zdjęciu?
        </p>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b' }}>
          Przeciągnij plik tutaj lub kliknij – zapiszesz bez wpisywania danych
        </p>
        <input
          type="file"
          accept=".pdf,image/jpeg,image/png,image/webp,image/heic"
          multiple
          onChange={handleFileOnlyUpload}
          disabled={uploadingOnly}
          style={{ display: 'none' }}
          id="file-only-input"
        />
        <label
          htmlFor="file-only-input"
          style={{
            display: 'inline-block',
            padding: '10px 20px',
            borderRadius: 10,
            background: 'linear-gradient(135deg, #6B7FFF 0%, #5A6EE6 100%)',
            color: 'white',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {uploadingOnly ? 'Zapisywanie…' : 'Dodaj plik (PDF, zdjęcie)'}
        </label>
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit}
          style={{
            background: '#f8fafc',
            padding: 20,
            borderRadius: 16,
            marginBottom: 24,
            border: '1px solid #e2e8f0',
          }}
        >
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>{editingId ? 'Edytuj wynik' : 'Nowy wynik'}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Data badania</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid #e2e8f0', fontSize: 14 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Typ</label>
              <select
                value={formData.type}
                onChange={(e) => {
                  const t = e.target.value as LabResultType;
                  setFormData({
                    ...formData,
                    type: t,
                    typeOther: t !== 'inne' ? '' : formData.typeOther,
                    unit: t !== 'inne' ? labResultsService.getDefaultUnit(t) : '',
                  });
                }}
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid #e2e8f0', fontSize: 14 }}
              >
                {(['kalprotectyna', 'crp', 'inne'] as const).map((t) => (
                  <option key={t} value={t}>{TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>
            {formData.type === 'inne' && (
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Czego dotyczy ten wynik? *</label>
                <input
                  type="text"
                  value={formData.typeOther}
                  onChange={(e) => setFormData({ ...formData, typeOther: e.target.value })}
                  placeholder="np. hemoglobina, ferrytyna"
                  required={formData.type === 'inne'}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid #e2e8f0', fontSize: 14 }}
                />
              </div>
            )}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Wartość</label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder="np. 120"
                required
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid #e2e8f0', fontSize: 14 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Jednostka</label>
              <input
                type="text"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                placeholder={formData.type !== 'inne' ? labResultsService.getDefaultUnit(formData.type) : 'np. mg/L'}
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid #e2e8f0', fontSize: 14 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Notatki (opcjonalnie)</label>
              <input
                type="text"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="np. przed zmianą leku"
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '2px solid #e2e8f0', fontSize: 14 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Załączniki (PDF, zdjęcia)</label>
              <input
                type="file"
                accept=".pdf,image/jpeg,image/png,image/webp,image/heic"
                multiple
                onChange={handleFileSelect}
                disabled={attachmentLoading}
                style={{ width: '100%', fontSize: 14 }}
              />
              <p style={{ margin: '4px 0 0', fontSize: 11, color: '#94a3b8' }}>Max 10 MB na plik. Dozwolone: PDF, JPG, PNG, WebP, HEIC.</p>
              {(pendingFiles.length > 0 || attachments.length > 0) && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {pendingFiles.map((f, idx) => (
                    <div key={`p-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <span style={{ flex: 1, color: '#64748b' }}>{f.name}</span>
                      <button type="button" onClick={() => removePendingFile(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 12 }}>Usuń</button>
                    </div>
                  ))}
                  {attachments.map((a) => (
                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                      <button type="button" onClick={() => handleDownloadAttachment(a)} style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', textDecoration: 'underline' }}>
                        {a.fileName}
                      </button>
                      <button type="button" onClick={() => handleDeleteAttachment(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 12 }}>Usuń</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                type="submit"
                style={{
                  padding: '10px 20px',
                  borderRadius: 10,
                  border: 'none',
                  background: 'linear-gradient(135deg, #6B7FFF 0%, #5A6EE6 100%)',
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {editingId ? 'Zapisz' : 'Dodaj'}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); resetForm(); }}
                style={{
                  padding: '10px 20px',
                  borderRadius: 10,
                  border: '2px solid #e2e8f0',
                  background: 'white',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: '#64748b',
                }}
              >
                Anuluj
              </button>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <p style={{ color: '#64748b' }}>Ładowanie...</p>
      ) : results.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#64748b', fontSize: 14 }}>
          Brak zapisanych wyników. Kliknij „Dodaj wynik”, aby dodać kalprotectynę, CRP lub inne badanie.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {results.map((r) => (
            <div
              key={r.id}
              style={{
                padding: 16,
                borderRadius: 12,
                border: '1px solid #e2e8f0',
                background: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 12,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 15, color: '#0f172a' }}>
                  {r.type === 'inne' && r.typeOther === 'Załącznik'
                    ? 'Wynik w załączniku'
                    : r.type === 'inne' && r.typeOther
                      ? r.typeOther
                      : TYPE_LABELS[r.type]}
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                  {r.type === 'inne' && r.typeOther === 'Załącznik' ? (
                    <>
                      {r.date}
                      {(attachmentsByResult[r.id]?.length ?? 0) > 0 && (
                        <span style={{ marginLeft: 6, color: '#6B7FFF', fontWeight: 500 }}>
                          · {(attachmentsByResult[r.id]?.length ?? 0)} plik(ów)
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      {r.date} · {r.value} {r.unit || ''}
                      {r.notes && ` · ${r.notes}`}
                      {(attachmentsByResult[r.id]?.length ?? 0) > 0 && (
                        <span style={{ marginLeft: 6, color: '#6B7FFF', fontWeight: 500 }}>
                          · {(attachmentsByResult[r.id]?.length ?? 0)} załącznik(ów)
                        </span>
                      )}
                    </>
                  )}
                </div>
                {(attachmentsByResult[r.id]?.length ?? 0) > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {(attachmentsByResult[r.id] || []).map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => handleDownloadAttachment(a)}
                        style={{
                          textAlign: 'left',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#2563eb',
                          textDecoration: 'underline',
                          fontSize: 13,
                          padding: 0,
                        }}
                      >
                        📎 {a.fileName}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => handleEdit(r)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    border: '2px solid #e2e8f0',
                    background: 'white',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    color: '#64748b',
                  }}
                >
                  Edytuj
                </button>
                <button
                  onClick={() => handleDelete(r.id)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    border: 'none',
                    background: '#fee2e2',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    color: '#dc2626',
                  }}
                >
                  Usuń
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
